/**
 * Date utility functions - All dates displayed in Australia/Sydney timezone (AEDT/AEST)
 */

const TIMEZONE = 'Australia/Sydney';
const LOCALE = 'en-AU';

/**
 * Format a date/timestamp for display with time (e.g., "6 Feb 2026, 09:40 am")
 */
export function formatDateTime(date) {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleDateString(LOCALE, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: TIMEZONE
    });
}

/**
 * Format a date/timestamp for short display with time (e.g., "6 Feb, 09:40 am")
 */
export function formatDateTimeShort(date) {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleDateString(LOCALE, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: TIMEZONE
    });
}

/**
 * Format a date for display without time (e.g., "6 Feb 2026")
 */
export function formatDateOnly(date) {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleDateString(LOCALE, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: TIMEZONE
    });
}

/**
 * Format a date for short display without time (e.g., "6 Feb")
 */
export function formatDateShort(date) {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleDateString(LOCALE, {
        day: 'numeric',
        month: 'short',
        timeZone: TIMEZONE
    });
}

/**
 * Format for print tickets - intake date (e.g., "06 Feb 2026")
 */
export function formatIntakeDate(date) {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleDateString(LOCALE, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: TIMEZONE
    });
}

/**
 * Format for due dates (e.g., "Fri, 06 Feb")
 */
export function formatDueDate(date) {
    if (!date) return 'Not set';
    const d = new Date(date);
    return d.toLocaleDateString(LOCALE, {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        timeZone: TIMEZONE
    });
}

/**
 * Format for due date with year (e.g., "Fri, 06 Feb 2026")
 */
export function formatDueDateFull(date) {
    if (!date) return 'Not set';
    const d = new Date(date);
    return d.toLocaleDateString(LOCALE, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: TIMEZONE
    });
}

/**
 * Format full date+time for print tickets (e.g., "6/2/2026, 9:40:00 am")
 */
export function formatFullDateTime(date) {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleString(LOCALE, { timeZone: TIMEZONE });
}

/**
 * Format for print - long date (e.g., "6 February 2026")
 */
export function formatLongDate(date) {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleDateString(LOCALE, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: TIMEZONE
    });
}

/**
 * Format time only (e.g., "9:40:00 am")
 */
export function formatTimeOnly(date) {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleTimeString(LOCALE, { timeZone: TIMEZONE });
}

/**
 * Format date only for display (e.g., "06/02/2026")
 */
export function formatDateLocale(date) {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleDateString(LOCALE, { timeZone: TIMEZONE });
}

/**
 * Get YYYY-MM-DD string in Sydney timezone (for date input values)
 */
export function toDateInputValue(date) {
    if (!date) return '';
    const d = new Date(date);
    // Get Sydney date parts
    const parts = d.toLocaleDateString(LOCALE, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: TIMEZONE
    }).split('/');
    // en-AU format is DD/MM/YYYY, convert to YYYY-MM-DD
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
}

/**
 * Get today's date as YYYY-MM-DD in Sydney timezone
 */
export function getTodayDateString() {
    return toDateInputValue(new Date());
}

/**
 * Format for date with optional year (used in message inbox etc.)
 */
export function formatDateCompact(date) {
    if (!date) return '—';
    const d = new Date(date);
    const now = new Date();
    const nowYear = parseInt(now.toLocaleDateString(LOCALE, { year: 'numeric', timeZone: TIMEZONE }));
    const dateYear = parseInt(d.toLocaleDateString(LOCALE, { year: 'numeric', timeZone: TIMEZONE }));
    
    return d.toLocaleDateString(LOCALE, {
        day: '2-digit',
        month: 'short',
        year: dateYear !== nowYear ? 'numeric' : undefined,
        timeZone: TIMEZONE
    });
}

/**
 * Get an ISO string adjusted to Sydney timezone (for storage timestamps created client-side)
 * Note: For server-stored timestamps, prefer serverTimestamp() in Firestore
 */
export function getSydneyISOString() {
    return new Date().toISOString();
}

export { TIMEZONE, LOCALE };
